"use client";

import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";
import Image from "next/image";
import {
  ArrowRight,
  Mail,
  Phone,
  MessageCircle,
  Headphones,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import HeroBackgroundSketch from "./HeroBackgroundSketch";

const FEATURE_TITLE_CLASS = "text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight leading-tight";
const FEATURE_BODY_CLASS = "text-base sm:text-lg md:text-xl leading-relaxed break-keep text-balance";
const FEATURE_CARD_TITLE_CLASS = "text-2xl sm:text-3xl font-semibold leading-tight tracking-tight";
const FEATURE_CARD_BODY_CLASS = "mt-4 space-y-2 text-base sm:text-lg leading-relaxed";
const FEATURE_TEXT_CONTAINER_CLASS = "flex flex-col justify-center space-y-6 max-w-[520px] w-full text-left";

// ConnectTone Landing Page — Toss-style info sections with music emotion focus
// Palette: Primary Purple (#A78BFA), Gold Pink (#FBCFE8), Deep Navy (#1E1B4B)

export default function MainLandingPage() {
  // 인트로 애니메이션 비활성화
  const [showIntro, setShowIntro] = useState(false);
  const [showContent, setShowContent] = useState(true);
  const [currentText, setCurrentText] = useState(0);
  const [typingText, setTypingText] = useState("");
  const [showBlackhole, setShowBlackhole] = useState(false);
  const typingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // useEffect(() => {
  //   // 영화 예고편처럼 한 문장씩 순차적으로 나타나는 시퀀스
  //   // 연결하다 -> 성장하다 -> 내 음악의 성장, -> 커넥톤에서. (타이핑 효과)
  //   const timers: NodeJS.Timeout[] = [];

  //   // 각 텍스트 표시 (한 문장씩 순차적으로)
  //   timers.push(setTimeout(() => setCurrentText(1), 1000));   // 연결하다 (1초)
  //   timers.push(setTimeout(() => setCurrentText(2), 3200));   // 성장하다 (3.2초)
  //   timers.push(setTimeout(() => setCurrentText(3), 5400));   // 내 음악의 성장, (5.4초)
    
  //   // 커넥톤에서. 타이핑 효과
  //   const typingTimer = setTimeout(() => {
  //     setCurrentText(4);
  //     const fullText = "커넥톤에서.";
  //     let currentIndex = 0;
  //     typingIntervalRef.current = setInterval(() => {
  //       if (currentIndex < fullText.length) {
  //         setTypingText(fullText.slice(0, currentIndex + 1));
  //         currentIndex++;
  //       } else {
  //         if (typingIntervalRef.current) {
  //           clearInterval(typingIntervalRef.current);
  //           typingIntervalRef.current = null;
  //         }
  //         // 타이핑 완료 후 텀 주고 블랙홀 효과 시작
  //         timers.push(setTimeout(() => {
  //           setShowBlackhole(true);
  //         }, 800)); // 0.8초 텀
  //       }
  //     }, 120); // 타이핑 속도
  //   }, 7600); // 7.6초에 시작
  //   timers.push(typingTimer);

  //   // 블랙홀 효과 후 본 컨텐츠 페이드인 시작
  //   timers.push(setTimeout(() => {
  //     setShowContent(true);
  //   }, 11000)); // 11초 후 본 컨텐츠 페이드인

  //   // 인트로 종료
  //   timers.push(setTimeout(() => {
  //     setShowIntro(false);
  //   }, 12000)); // 12초 후 인트로 종료

  //   return () => {
  //     timers.forEach(timer => clearTimeout(timer));
  //     if (typingIntervalRef.current) {
  //       clearInterval(typingIntervalRef.current);
  //       typingIntervalRef.current = null;
  //     }
  //   };
  // }, []);

  return (
    <div className="min-h-screen w-full text-white bg-[#0c0c10] relative overflow-x-hidden">
      {/* 영화 같은 인트로 스크린 */}
      <AnimatePresence>
        {showIntro && (
          <motion.div
            initial={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0 }}
            animate={{
              scale: showBlackhole ? [1, 0.8, 0] : 1,
              opacity: showBlackhole ? [1, 0.8, 0] : 1
            }}
            transition={{ 
              duration: showBlackhole ? 1.5 : 0.8,
              ease: showBlackhole ? "easeIn" : "easeInOut"
            }}
            className="fixed inset-0 z-[100] flex items-center justify-center origin-center"
            style={{
              background: "linear-gradient(135deg, #280a49 0%, #43127b 50%, #7724d5 100%)"
            }}
          >
            {/* 빛이 은은하게 나오는 효과 - 히어로와 자연스럽게 이어지도록 */}
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ 
                scale: showBlackhole 
                  ? [1.8, 0.5, 0] 
                  : [0, 1.2, 1.8, 1.8],
                opacity: showBlackhole
                  ? [0.6, 0.8, 0]
                  : [0, 0.8, 0.6, 0.6]
              }}
              transition={{ 
                duration: showBlackhole ? 1.5 : 8,
                ease: showBlackhole ? "easeIn" : "easeOut",
                opacity: {
                  times: showBlackhole ? [0, 0.3, 1] : [0, 0.2, 0.7, 1],
                  duration: showBlackhole ? 1.5 : 8
                }
              }}
              className="absolute inset-0 flex items-center justify-center"
            >
              <div
                className="w-[1000px] h-[1000px] rounded-full"
                style={{
                  background: "radial-gradient(circle, rgba(167,139,250,0.5) 0%, rgba(251,207,232,0.3) 30%, rgba(167,139,250,0.1) 60%, rgba(167,139,250,0) 80%)",
                  filter: "blur(150px)",
                }}
              />
            </motion.div>

            {/* 화려하게 쫘라라라 퍼지는 빛들 - 블랙홀처럼 빨려들어가는 효과 */}
            {[...Array(12)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ 
                  scale: 0, 
                  opacity: 0,
                  x: 0,
                  y: 0,
                  rotate: 0
                }}
                animate={{
                  scale: showBlackhole 
                    ? [2, 0.3, 0]
                    : [0, 1.2, 2, 2],
                  opacity: showBlackhole
                    ? [0.4, 0.6, 0]
                    : [0, 0.6, 0.4, 0.4],
                  x: showBlackhole
                    ? [Math.cos((i * Math.PI * 2) / 12) * 800, 0, 0]
                    : [0, Math.cos((i * Math.PI * 2) / 12) * 800, Math.cos((i * Math.PI * 2) / 12) * 800],
                  y: showBlackhole
                    ? [Math.sin((i * Math.PI * 2) / 12) * 800, 0, 0]
                    : [0, Math.sin((i * Math.PI * 2) / 12) * 800, Math.sin((i * Math.PI * 2) / 12) * 800],
                  rotate: showBlackhole
                    ? [360, 720, 1080]
                    : [0, 360, 360],
                }}
                transition={{
                  duration: showBlackhole ? 1.5 : 8,
                  ease: showBlackhole ? "easeIn" : "easeOut",
                  delay: showBlackhole ? i * 0.03 : 0.8 + i * 0.08,
                  opacity: {
                    times: showBlackhole ? [0, 0.3, 1] : [0, 0.2, 0.6, 1],
                    duration: showBlackhole ? 1.5 : 8
                  }
                }}
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 rounded-full"
                style={{
                  background: `radial-gradient(circle, ${
                    i % 3 === 0 ? "#A78BFA" : i % 3 === 1 ? "#FBCFE8" : "#8B5CF6"
                  } 0%, rgba(167,139,250,0) 70%)`,
                  filter: "blur(60px)",
                }}
              />
            ))}

            {/* 메인 텍스트 - 한 문장씩 순차적으로 나타나는 영화 예고편 스타일 */}
            <div className="relative z-10 text-center px-4">
              <div className="min-h-[200px] flex items-center justify-center">
                <AnimatePresence mode="wait">
                  {/* 첫 번째: 연결하다 */}
                  {currentText === 1 && (
                    <motion.h1
                      key="connect"
                      initial={{ opacity: 0, y: 50, scale: 0.8 }}
                      animate={{ 
                        opacity: 1,
                        y: 0,
                        scale: 1
                      }}
                      exit={{ 
                        opacity: 0,
                        y: -30,
                        scale: 0.9,
                        transition: { duration: 0.5 }
                      }}
                      transition={{
                        duration: 0.8,
                        ease: "easeOut"
                      }}
                      className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl font-bold bg-gradient-to-r from-[#A78BFA] via-[#FBCFE8] to-[#A78BFA] bg-clip-text text-transparent"
                    >
                      연결하다
                    </motion.h1>
                  )}

                  {/* 두 번째: 성장하다 */}
                  {currentText === 2 && (
                    <motion.h1
                      key="grow"
                      initial={{ opacity: 0, y: 50, scale: 0.8 }}
                      animate={{ 
                        opacity: 1,
                        y: 0,
                        scale: 1
                      }}
                      exit={{ 
                        opacity: 0,
                        y: -30,
                        scale: 0.9,
                        transition: { duration: 0.5 }
                      }}
                      transition={{
                        duration: 0.8,
                        ease: "easeOut"
                      }}
                      className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl font-bold bg-gradient-to-r from-[#FBCFE8] via-[#A78BFA] to-[#FBCFE8] bg-clip-text text-transparent"
                    >
                      성장하다
                    </motion.h1>
                  )}

                  {/* 세 번째: 내 음악의 성장, */}
                  {currentText === 3 && (
                    <motion.h1
                      key="music-growth"
                      initial={{ opacity: 0, y: 50, scale: 0.8 }}
                      animate={{ 
                        opacity: 1,
                        y: 0,
                        scale: 1
                      }}
                      exit={{ 
                        opacity: 0,
                        y: -30,
                        scale: 0.9,
                        transition: { duration: 0.5 }
                      }}
                      transition={{
                        duration: 0.8,
                        ease: "easeOut"
                      }}
                      className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl font-bold bg-gradient-to-r from-[#8B5CF6] via-[#FBCFE8] to-[#8B5CF6] bg-clip-text text-transparent"
                    >
                      내 음악의 성장,
                    </motion.h1>
                  )}

                  {/* 네 번째: 커넥톤에서. (타이핑 효과) */}
                  {currentText === 4 && (
                    <motion.h1
                      key="connectone"
                      initial={{ opacity: 0, y: 50, scale: 0.8 }}
                      animate={{ 
                        opacity: showBlackhole ? 0 : 1,
                        y: showBlackhole ? 0 : 0,
                        scale: showBlackhole ? 0 : 1
                      }}
                      exit={{ 
                        opacity: 0,
                        scale: 0,
                        transition: { duration: 0.6 }
                      }}
                      transition={{
                        duration: showBlackhole ? 0.6 : 0.8,
                        ease: showBlackhole ? "easeIn" : "easeOut"
                      }}
                      className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl font-bold bg-gradient-to-r from-[#A78BFA] via-[#FBCFE8] to-[#A78BFA] bg-clip-text text-transparent"
                    >
                      {typingText || "커넥톤에서."}
                      {typingText.length > 0 && typingText.length < 6 && !showBlackhole && (
                        <motion.span
                          animate={{ opacity: [1, 0] }}
                          transition={{ duration: 0.6, repeat: Infinity, repeatType: "reverse" }}
                          className="ml-1"
                        >
                          |
                        </motion.span>
                      )}
                    </motion.h1>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 본 컨텐츠 - 인트로와 자연스럽게 이어지는 페이드인 */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: showContent ? 1 : 0 }}
        transition={{ 
          duration: 1.5,
          ease: "easeInOut"
        }}
        className="relative"
        style={{
          opacity: showContent ? 1 : 0,
          pointerEvents: showContent ? "auto" : "none"
        }}
      >
        {/* Hero with YouTube video background */}
        <section
          className="relative pt-16 pb-24 sm:pb-32 md:pb-40 lg:pb-48 overflow-hidden min-h-[85vh] md:min-h-screen h-auto md:h-screen flex items-center md:items-end"
        >
          {/* Background Video */}
          <div className="absolute inset-0 w-full h-full overflow-hidden">
            <div className="relative w-full h-full">
              {/* Background Video - 16:9 aspect ratio, responsive */}
              <video
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full object-cover"
                src="/videos/main.mp4"
                autoPlay
                muted
                loop
                playsInline
                style={{ 
                  pointerEvents: 'none',
                  minWidth: '100%',
                  minHeight: '100%',
                  width: 'auto',
                  height: 'auto'
                }}
              />
              {/* Overlay - 영상이 보이도록 더 투명하게 */}
              <div className="absolute inset-0 bg-gradient-to-br from-darkMain/40 via-secondary/30 to-main/40" />
            </div>
          </div>

          <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 w-full">
            <HeroContent />
          </div>
        </section>

        <CategoryHighlights />
        <ArtistMatchSection />
        <BrandHeader />
        <MusicDNAExampleSection />
        <section className="py-12 sm:py-16 md:py-20 lg:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <InfoSection
              kicker="Pro Producer Review"
              title={
                <>
                  현직 프로들의 노하우,
                  <br />
                  <span className="text-point3">1:1 맞춤</span> 피드백
                </>
              }
              description={
                <>
                  <span className="whitespace-nowrap">프로들의 노하우가 담긴</span> 진짜 피드백으로 성장하다.
                  <br />
                  고민되는 부분도 상담 OK
                </>
              }
              ctaText="피드백 요청"
              note="48시간 이내 결과지 수령"
              reverse={false}
            >
              <ProducerVideoCard />
            </InfoSection>
          </div>
        </section>
        <LimitedOfferSection />

        {false && (
          <SectionWrapper gradientFrom="#1E1B4B" gradientTo="#2A2348">
            <InfoSection
              kicker="AI Emotion Insight"
              title={
                <>
                  AI가 내 음악에 담긴
                  <br />
                  <span className="text-[#FBCFE8]">감정</span>까지 읽어냅니다.
                </>
              }
              description={
                <>
                  테크닉, 다이나믹, 감정까지
                  <br />
                  내 음악에 담긴 <span className="whitespace-nowrap">감정의 언어를 시각화</span>합니다.
                </>
              }
              ctaText="분석하기"
              note=""
              reverse={false}
            >
              <ProducerFeedbackCard />
            </InfoSection>
          </SectionWrapper>
        )}

        {false && (
          <section
            className="py-16 sm:py-20 md:py-24 lg:py-32 border-0"
            style={{
              background:
                "linear-gradient(180deg, #4A4272 0%, #5A5478 15%, #6A6288 30%, #8A8696 45%, #A0A0B0 60%, #B8B8C8 70%, #D0D0D8 80%, #E0E0E8 90%, #F0F0F4 95%, #ffffff 100%)",
            }}
          >
            <div className="mx-auto max-w-7xl px-6">
              {/* Header */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-center mb-12"
              >
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  지금 시작하면, 음악이 쌓이고 혜택도 쌓입니다.
                </h2>
                <p
                  className="text-lg text-white/90 max-w-2xl mx-auto break-keep"
                  style={{ wordBreak: "keep-all", overflowWrap: "break-word" }}
                >
                  <span className="whitespace-nowrap">음악 활동을 할수록 포인트가 쌓여요.</span>{" "}
                  <span className="whitespace-nowrap">분석, 후기 작성, 서비스 참여 등으로</span>{" "}
                  당신의 음악 여정에 보상을 더하세요.
                </p>
              </motion.div>

              {/* Cards Grid - 2x2 토스 스타일 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12 max-w-4xl mx-auto">
                {[
                  {
                    coinText: "5000P",
                    title: "가입 즉시 5,000P 지급",
                    description: "지금 회원가입만 해도 바로 사용 가능한 포인트 제공",
                  },
                  {
                    coinText: "100P",
                    title: "서비스 참여 리워드",
                    description: "커넥톤의 음악 성장 활동에 참여하면 혜택이 쌓입니다",
                  },
                  {
                    coinText: "50P",
                    title: "후기 작성 50P 적립",
                    description: "경험을 나누면, 다음 음악인의 여정이 풍성해집니다",
                  },
                  {
                    coinText: "Grade",
                    title: "활동 등급별 추가 리워드",
                    description: "참여가 많을수록 더 큰 보상 제공",
                  },
                ].map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    whileHover={{ scale: 1.02 }}
                    className="bg-white rounded-2xl p-8 shadow-base hover:shadow-md transition-all duration-300 border border-gray-100 flex flex-col items-center text-center"
                  >
                    <div className="mb-6 flex items-center justify-center">
                      <div className="relative w-24 h-24 flex items-center justify-center">
                        <div className="absolute inset-0 bg-gradient-to-br from-point1 via-point2 to-secondary rounded-full shadow-lg"></div>
                        <div className="absolute inset-2 bg-gradient-to-br from-point to-point2 rounded-full"></div>
                        <div className="relative z-10 flex flex-col items-center justify-center">
                          <span className="text-lg font-bold text-white tracking-tight">
                            {benefit.coinText}
                          </span>
                        </div>
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4 leading-tight">
                      {benefit.title}
                    </h3>
                    <p className="text-base text-gray-600 leading-relaxed">
                      {benefit.description}
                    </p>
                  </motion.div>
                ))}
              </div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-center"
              >
                <Link href="/auth/login">
                  <motion.button
                    whileHover={{
                      scale: 1.02,
                      boxShadow: "0 4px 20px rgba(183,156,255,0.3)",
                    }}
                    whileTap={{ scale: 0.98 }}
                    className="relative px-10 py-4 rounded-full text-white transition-all duration-300 font-medium"
                    style={{
                      background: "rgba(30, 27, 75, 0.8)",
                      border: "1px solid rgba(183, 156, 255, 0.4)",
                      boxShadow: "0 2px 12px rgba(183, 156, 255, 0.2)",
                      fontSize: "20px",
                      fontWeight: "500",
                      letterSpacing: "-0.01em",
                    }}
                  >
                    지금 바로 시작
                  </motion.button>
                </Link>
              </motion.div>
            </div>
          </section>
        )}

      </motion.div>
    </div>
  );
}

function HeroContent() {
  return (
    <div className="flex flex-col items-start justify-center text-left max-w-5xl px-4 sm:px-6">
      {/* Kicker */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="mb-6"
      >
        <Kicker>AI Music Analysis</Kicker>
      </motion.div>

      {/* Title */}
      <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl leading-[1.2] md:leading-tight font-semibold tracking-tight mb-4 md:mb-8 text-left space-y-2 md:space-y-3 text-balance">
        <motion.span
          initial={{ opacity: 0, y: 30, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{
            duration: 0.8,
            delay: 0,
            ease: [0.16, 1, 0.3, 1],
          }}
          className="block bg-gradient-to-r from-point via-point2 to-point3 bg-clip-text text-transparent"
        >
          내 음악
        </motion.span>

        <motion.span
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.8,
            delay: 0.3,
            ease: [0.16, 1, 0.3, 1],
          }}
          className="block text-white"
        >
          <motion.span
            initial={{
              textShadow: "0 0 0px rgba(124, 58, 237, 0)",
              color: "rgba(255,255,255,0.5)",
            }}
            animate={{
              textShadow: [
                "0 0 20px rgba(124, 58, 237, 0.8)",
                "0 0 40px rgba(124, 58, 237, 1)",
                "0 0 30px rgba(124, 58, 237, 0.9)",
              ],
              color: "#FFFFFF",
            }}
            transition={{
              textShadow: {
                duration: 1.5,
                delay: 0.5,
                repeat: Infinity,
                repeatType: "reverse",
                ease: "easeInOut",
              },
              color: {
                duration: 0.6,
                delay: 0.5,
                ease: "easeOut",
              },
            }}
            className="font-bold bg-gradient-to-r from-purple-400 via-violet-400 to-purple-400 bg-clip-text text-transparent"
          >
            AI
          </motion.span>
          <span>가 분석하고</span>
        </motion.span>

        <motion.span
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.8,
            delay: 0.6,
            ease: [0.16, 1, 0.3, 1],
          }}
          className="block text-white"
        >
          <motion.span
            initial={{
              textShadow: "0 0 0px rgba(168, 85, 247, 0)",
              color: "rgba(255,255,255,0.5)",
            }}
            animate={{
              textShadow: [
                "0 0 20px rgba(168, 85, 247, 0.8)",
                "0 0 40px rgba(168, 85, 247, 1)",
                "0 0 30px rgba(168, 85, 247, 0.9)",
              ],
              color: "#FFFFFF",
            }}
            transition={{
              textShadow: {
                duration: 1.5,
                delay: 0.8,
                repeat: Infinity,
                repeatType: "reverse",
                ease: "easeInOut",
              },
              color: {
                duration: 0.6,
                delay: 0.8,
                ease: "easeOut",
              },
            }}
            className="font-bold bg-gradient-to-r from-pink-400 via-purple-400 to-pink-400 bg-clip-text text-transparent"
          >
            프로
          </motion.span>
          <span>와 성장하다</span>
        </motion.span>
      </h1>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
        className="text-white/95 text-base sm:text-lg md:text-xl font-medium leading-relaxed mb-6 md:mb-4 max-w-2xl break-keep text-left"
        style={{
          wordBreak: "keep-all",
          overflowWrap: "break-word",
          lineHeight: "1.6",
        }}
      >
        AI와 전문가가 함께 그리는 음악 지도
      </motion.p>

      <motion.div
        initial={{
          opacity: 0,
          y: 30,
          scale: 0.8,
          filter: "blur(10px)",
        }}
        animate={{
          opacity: 1,
          y: 0,
          scale: 1,
          filter: "blur(0px)",
        }}
        transition={{
          duration: 0.8,
          delay: 1.3,
          ease: [0.34, 1.56, 0.64, 1],
        }}
        className="mt-4 md:mt-6 mb-8 md:mb-12"
      >
        <Link href="/music-analysis/analyze">
          <motion.div
            initial={{ boxShadow: "0 0 0px rgba(168, 85, 247, 0)" }}
            animate={{
              boxShadow: [
                "0 0 20px rgba(168, 85, 247, 0.5)",
                "0 0 40px rgba(168, 85, 247, 0.8)",
                "0 0 30px rgba(168, 85, 247, 0.6)",
              ],
            }}
            transition={{
              duration: 2,
              delay: 1.5,
              repeat: Infinity,
              repeatType: "reverse",
            }}
          >
            <GlowButton>무료로 시작하기</GlowButton>
          </motion.div>
        </Link>
      </motion.div>

      {false && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6, ease: "easeOut" }}
          className="relative w-full max-w-2xl mx-auto px-4 sm:px-0"
        >
          <div className="relative rounded-3xl p-4 sm:p-6 md:p-8 bg-gradient-to-br from-darkMain to-main border border-white/10 shadow-2xl overflow-hidden">
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#A78BFA]/20 border border-[#A78BFA]/40">
                <Headphones className="w-5 h-5 text-[#A78BFA]" />
              </span>
              <div>
                <p className="text-base text-white/70">실시간 감정 스펙트럼</p>
                <p className="text-lg font-medium">AI Emotion Meter</p>
              </div>
            </div>
            <WaveformDemo />
          </div>
        </motion.div>
      )}
    </div>
  );
}

function CategoryHighlights() {
  const cards = [
    {
      title: "AI 음악 분석",
      description: [
        "AI가 내 음악 DNA를 분석합니다.",
        "심화 분석으로 종합 차트, 잠재력,",
        "추천 연습 루틴까지!",
      ],
      ctaText: "내 음악 진단받기",
      href: "/music-analysis/analyze",
      badgeLabel: "CONNECTONE AI",
      tone: "ai" as const,
      offsetClass: "",
    },
    {
      title: "프로 피드백",
      description: [
        "연습실까지 찾아갈 필요 없이,",
        "온라인에서 현직 프로에게",
        '"진짜" 피드백을 받아보세요',
      ],
      ctaText: "프로 피드백 받기",
      href: "/expert-feedback",
      badgeLabel: "CONNECTONE PRO",
      tone: "pro" as const,
      offsetClass: "",
    },
    // 프리미엄 레슨 카드 비활성화
    // {
    //   title: "프리미엄 레슨",
    //   description: [
    //     "언제 어디서나, 내가 원하는 방식으로!",
    //     "온라인부터 오프라인까지",
    //     "장르별 전문 프로의 코칭",
    //   ],
    //   ctaText: "Comming soon",
    //   href: "/lessons",
    //   badgeLabel: "ConnecTone Lesson",
    //   tone: "lesson" as const,
    //   offsetClass: "lg:translate-y-12",
    // },
  ];

  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: (index: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: index * 0.04,
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1] as const,
      },
    }),
  };

  return (
    <section className="bg-gradient-to-br from-darkMain to-secondary py-16 sm:py-20 md:py-24">
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6">
        <div className="flex flex-col gap-3">
          <span className="text-base font-semibold uppercase tracking-[0.24em] text-point3">
            커넥톤 핵심 서비스
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-white">
            분석부터 프로 피드백까지
          </h2>
          <p className="max-w-2xl text-base text-gray-300 sm:text-lg">
            필요한 서비스를 한 번에 비교하고, 지금 나에게 꼭 맞는 성장 경로를 선택하세요.
          </p>
        </div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.45 }}
          transition={{ staggerChildren: 0.12 }}
          className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-8 lg:grid-cols-2 lg:gap-8"
        >
          {cards.map((card, index) => (
            <motion.div
              key={card.title}
              custom={index}
              variants={cardVariants}
              className={`transform ${card.offsetClass}`}
            >
              <CategoryCard {...card} />
            </motion.div>
          ))}
        </motion.div>
      </div>

    </section>
  );
}

type CategoryCardProps = {
  title: string;
  description: string | string[];
  ctaText: string;
  href: string;
  badgeLabel: string;
  tone: "ai" | "pro" | "lesson" | "realtime";
  tagLabel?: string;
};

function CategoryCard({
  title,
  description,
  ctaText,
  href,
  badgeLabel,
  tone,
  tagLabel,
}: CategoryCardProps) {
  const accentMap = {
    ai: {
      badge: "from-[#22D3EE] via-[#6366F1] to-[#A855F7]",
      link: "text-[#7C3AED]",
    },
    pro: {
      badge: "from-[#F472B6] via-[#C084FC] to-[#8B5CF6]",
      link: "text-[#EC4899]",
    },
    lesson: {
      badge: "from-[#34D399] via-[#60A5FA] to-[#8B5CF6]",
      link: "text-[#10B981]",
    },
    realtime: {
      badge: "from-[#818CF8] via-[#A78BFA] to-[#C084FC]",
      link: "text-[#A78BFA]",
    },
  } as const;

  const accent = accentMap[tone];

  return (
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ type: "spring", stiffness: 260, damping: 24 }}
      className="relative flex h-full min-h-[400px] flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] px-8 py-10 sm:px-9 sm:py-12"
    >
      {tagLabel && (
        <span className="absolute right-0 top-6 rounded-full bg-[#7C3AED] px-4 py-1 text-base font-semibold uppercase tracking-wide text-white">
          {tagLabel}
        </span>
      )}

      <div className="relative z-[2] flex flex-1 flex-col gap-5">
        <span
          className={`inline-flex w-fit items-center justify-center rounded-lg border-2 bg-transparent px-3 py-1 text-base font-semibold uppercase tracking-wide`}
          style={{
            borderColor:
              tone === "ai"
                ? "#22D3EE"
                : tone === "pro"
                  ? "#F472B6"
                  : tone === "realtime"
                    ? "#A78BFA"
                    : "#34D399",
            color:
              tone === "ai"
                ? "#22D3EE"
                : tone === "pro"
                  ? "#F472B6"
                  : tone === "realtime"
                    ? "#A78BFA"
                    : "#34D399",
          }}
        >
          {badgeLabel}
        </span>
        <h3 className={`${FEATURE_CARD_TITLE_CLASS} text-white`}>
          {title}
        </h3>
        <div className={`${FEATURE_CARD_BODY_CLASS} text-white/70`}>
          {Array.isArray(description)
            ? description.map((line, idx) => <p key={idx}>{line}</p>)
            : <p>{description}</p>}
        </div>
      </div>

      <Link href={href} className="mt-auto pt-6">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className={`w-full py-3 px-6 rounded-full bg-gradient-to-r ${accent.badge} text-white font-semibold text-base transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2`}
        >
          {ctaText}
          {tone !== "lesson" && (
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          )}
        </motion.button>
      </Link>
    </motion.div>
  );
}

function LimitedOfferSection() {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const updateScrollState = () => {
      const { scrollLeft, clientWidth, scrollWidth } = el;
      setCanScrollLeft(scrollLeft > 4);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 4);
    };

    updateScrollState();
    el.addEventListener("scroll", updateScrollState);
    window.addEventListener("resize", updateScrollState);

    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, []);

  const scrollByCards = (direction: number) => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const firstChild = el.querySelector<HTMLElement>("[data-offer-card]");
    const cardWidth = firstChild?.getBoundingClientRect().width ?? el.clientWidth;
    const gap = 24; // matches gap-6
    el.scrollBy({ left: direction * (cardWidth + gap), behavior: "smooth" });
  };

  const offers = [
    {
      title: "AI 프리미엄 분석 리포트",
      category: "작곡/프로듀서",
      image: "/pro1.png",
    },
    {
      title: "프로 1:1 피드백 세션",
      category: "피아노",
      image: "/pro2.png",
    },
    {
      title: "프리미엄 레슨 패스",
      category: "가야금",
      image: "/pro3.png",
    },
    {
      title: "바이올린 마스터클래스",
      category: "바이올린",
      image: "/pro4.png",
    },
    {
      title: "보컬 톤 업그레이드 세션",
      category: "보컬",
      image: "/pro5.png",
    },
  ];

  return (
    <section className="bg-[#12121a] py-16 sm:py-20 md:py-24">
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex flex-col gap-3">
            <span className="text-base font-semibold uppercase tracking-[0.24em] text-white/90">
              커넥톤 프로의 Real 피드백
            </span>
            <h2 className="text-3xl font-semibold text-white sm:text-4xl">
              현직 프로의 피드백을
              <br />
              지금 바로 경험해 보세요
            </h2>
          </div>
          <Link
            href="/expert-feedback"
            className="inline-flex items-center justify-center rounded-full border border-[#7C3AED]/20 bg-[#7C3AED] px-5 py-2 text-base font-semibold text-white shadow-base transition hover:shadow-lg hover:bg-[#5b21b6]"
          >
            프로 프로필 보러가기
          </Link>
        </div>

        <div className="relative mt-12">
          <motion.button
            type="button"
            aria-label="이전 프로 카드"
            onClick={() => scrollByCards(-1)}
            disabled={!canScrollLeft}
            whileTap={{ scale: 0.95 }}
            className={`hidden sm:flex absolute left-2 top-1/2 z-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/40 bg-white/80 p-3 shadow-lg backdrop-blur ${
              canScrollLeft ? "text-[#4C1D95]" : "text-gray-400 cursor-not-allowed opacity-50"
            }`}
          >
            <ChevronLeft className="h-5 w-5" />
          </motion.button>

          <motion.button
            type="button"
            aria-label="다음 프로 카드"
            onClick={() => scrollByCards(1)}
            disabled={!canScrollRight}
          whileTap={{ scale: 0.95 }}
            className={`hidden sm:flex absolute right-2 top-1/2 z-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/40 bg-white/80 p-3 shadow-lg backdrop-blur ${
              canScrollRight ? "text-[#4C1D95]" : "text-gray-400 cursor-not-allowed opacity-50"
            }`}
          >
            <ChevronRight className="h-5 w-5" />
          </motion.button>

          <div
            ref={scrollContainerRef}
            className="flex gap-6 overflow-x-auto pb-4 sm:gap-8 sm:px-4 md:px-6 lg:px-0"
            style={{ scrollbarWidth: "none" }}
            data-scroll-container
          >
            <style>{`
              [data-scroll-container]::-webkit-scrollbar { display: none; }
            `}</style>
          {offers.map((offer, index) => (
            <motion.div
              key={offer.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.6, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="group flex h-full min-w-[260px] max-w-[320px] flex-col overflow-hidden rounded-[30px] bg-white shadow-[0_24px_90px_rgba(15,23,42,0.12)] sm:min-w-[280px] sm:max-w-[320px]"
              data-offer-card
            >
              <div className="relative w-full overflow-hidden" style={{ aspectRatio: '8/13' }}>
                <Image
                  src={offer.image}
                  alt={offer.title}
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, 33vw"
                  priority={index === 0}
                />
                <span className="absolute right-5 top-5 rounded-full bg-[#7C3AED] px-4 py-1 text-base font-semibold text-white shadow-lg shadow-[#7C3AED]/30">
                  PRO
                </span>
              </div>

              <div className="flex items-center justify-between px-7 py-4 sm:px-8">
                <span className="text-base font-semibold text-gray-900 sm:text-base">
                  {offer.category}
                </span>
                <ArrowRight className="h-4 w-4 text-[#7C3AED] transition-transform group-hover:translate-x-1" />
              </div>
              <div className="border-t border-gray-100 px-7 py-4 text-base text-gray-600 sm:px-8">
                <span>
                  지금, 현직 프로가 직접 남긴
                  <span className="bg-gradient-to-r from-[#F472B6] via-[#C084FC] to-[#7C3AED] bg-clip-text text-transparent text-lg font-semibold sm:text-xl">
                    {" "}REAL 피드백
                  </span>
                </span>
              </div>
            </motion.div>
          ))}
          </div>
        </div>
      </div>

      <motion.div
        className="mt-8 text-center text-lg font-semibold text-white sm:text-xl"
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      >
        현직 프로가 전해주는 진짜 피드백으로, 지금 성장해보세요!
      </motion.div>
    </section>
  );
}

function ArtistMatchSection() {
  return (
    <section className="relative overflow-hidden bg-[#F6F5FF] py-16 sm:py-20 md:py-24">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="mx-auto flex w-full max-w-6xl flex-col gap-12 px-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between"
      >
        <div className={`${FEATURE_TEXT_CONTAINER_CLASS} mx-auto lg:mx-0`}>
          <Kicker>AI Music DNA</Kicker>
          <h2 className={`${FEATURE_TITLE_CLASS} text-gray-900 lg:whitespace-nowrap`}>
            내 음악에도 MBTI가 있을까?
          </h2>
          <p className={`${FEATURE_BODY_CLASS} text-gray-600`}>
            <span className="block sm:inline">AI가 음악을 듣고, 당신의 음악 성향을 분석합니다.</span>{" "}
            <span className="block sm:inline">지금 내 음악 DNA를 확인해 보세요.</span>
          </p>
          <Link href="/music-analysis/analyze" className="inline-flex pt-2">
            <motion.button
              whileHover={{ scale: 1.03, boxShadow: "0 0 40px rgba(124,58,237,0.35)" }}
              whileTap={{ scale: 0.98 }}
              className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-[#4338ca] via-[#7c3aed] to-[#a855f7] px-6 py-3 text-base md:text-base font-semibold text-white shadow-lg shadow-[#7c3aed]/40 transition"
            >
              지금 분석하기
              <ArrowRight className="h-4 w-4" />
            </motion.button>
          </Link>
        </div>
        <ArtistSlotMachine />
      </motion.div>
    </section>
  );
}

const DNA_REEL_ITEMS = [
  { label: "감성파" },
  { label: "리듬메이커" },
  { label: "섬세파" },
  { label: "내면파" },
  { label: "에너지파" },
  { label: "감각파" },
  { label: "콘셉트파" },
  { label: "즉흥파" },
];

function ArtistSlotMachine() {
  const reels = [DNA_REEL_ITEMS, [...DNA_REEL_ITEMS].reverse(), DNA_REEL_ITEMS];

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 24 }}
      whileInView={{ opacity: 1, scale: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className="relative w-full max-w-md min-h-[420px] mx-auto lg:mx-0"
    >
      <div className="relative overflow-hidden rounded-[38px] bg-gradient-to-br from-[#0F172A] via-[#1E1B4B] to-[#312E81] p-1 shadow-[0_24px_80px_rgba(15,23,42,0.35)]">
        <div className="relative rounded-[36px] bg-[#090f1f] px-10 py-12 h-full flex flex-col justify-center">
          <div className="absolute inset-x-8 top-8 h-[1px] bg-gradient-to-r from-transparent via-[#A855F7]/50 to-transparent" />
          <div className="absolute inset-x-8 bottom-8 h-[1px] bg-gradient-to-r from-transparent via-[#A855F7]/50 to-transparent" />
          <div className="relative mx-auto flex flex-col items-center gap-6">
            <span className="text-base font-semibold uppercase tracking-[0.35em] text-[#A78BFA]">
              Music DNA Reel
            </span>
            <div className="relative flex w-full max-w-base items-center justify-center rounded-[28px] border border-white/10 bg-[#0F172A]/90 px-6 py-8">
              <div className="absolute inset-y-0 left-6 w-[12px] rounded-full bg-white/10" />
              <div className="absolute inset-y-0 right-6 w-[12px] rounded-full bg-white/10" />
              <div className="grid h-40 w-full grid-cols-3 gap-4 overflow-hidden rounded-2xl bg-[#111b2f] p-3 shadow-inner shadow-black/40">
                {reels.map((items, reelIndex) => (
                  <motion.ul
                    key={reelIndex}
                    className="flex flex-col items-center justify-start gap-3 text-white"
                    initial={{ y: 0 }}
                    animate={{
                      y: [0, -items.length * 64, 0],
                    }}
                    transition={{
                      duration: 7 + reelIndex * 1.2,
                      repeat: Infinity,
                      ease: "easeInOut",
                      repeatDelay: 0.4,
                    }}
                  >
                    {[...items, ...items, ...items.slice(0, 3)].map(
                      (item, idx) => (
                        <li
                          key={`${item.label}-${idx}`}
                          className="flex h-16 w-full items-center justify-center rounded-xl bg-white/[0.06] px-4 text-sm font-medium tracking-wide text-white/90"
                        >
                          {item.label}
                        </li>
                      )
                    )}
                  </motion.ul>
                ))}
              </div>
              <div className="absolute inset-x-0 top-1/2 h-14 -translate-y-1/2 bg-gradient-to-b from-[#0B1220]/70 via-transparent to-[#0B1220]/70" />
            </div>
            <div className="flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-5 py-2 text-base text-white/70">
              <span>Music DNA 결과가 곧 공개됩니다</span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function MusicDNAExampleSection() {
  return (
    <section className="py-16 sm:py-20 md:py-24 bg-[#f5f2ff]">
      <div className="mx-auto flex w-full max-w-6xl flex-col-reverse gap-12 px-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
        <div className="relative min-h-[420px] w-full max-w-md overflow-hidden rounded-[36px] border border-white/60 bg-gradient-to-br from-[#4338ca] via-[#7c3aed] to-[#6d28d9] shadow-[0_30px_80px_rgba(124,58,237,0.4)] mx-auto lg:mx-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,#fff/35,transparent_70%)]" />
          <div className="absolute inset-0 bg-[#0f172a]/40" />
          <Image
            src="/AI_result.png"
            alt="AI 분석 결과 예시"
            fill
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            priority
            className="object-cover blur-[2px] scale-[1.05] opacity-80"
          />
          <div className="relative flex h-full flex-col justify-between p-7 sm:p-9 text-white">
            <div className="inline-flex w-fit items-center gap-2 rounded-full bg-white/20 px-4 py-1.5 text-base font-semibold uppercase tracking-[0.25em]">
              분석 결과 예시
            </div>
            <div className="space-y-4 text-white/80">
              <p className="text-base">실제 화면은 더 선명하고 인터랙티브하게 제공됩니다.</p>
              <p className="text-base">AI가 자동으로 분석 리포트를 정리해 드려요.</p>
            </div>
          </div>
        </div>
        <div className={`${FEATURE_TEXT_CONTAINER_CLASS} mx-auto lg:mx-0 lg:pl-6`}>
          <Kicker>AI Music DNA</Kicker>
          <h2 className={`${FEATURE_TITLE_CLASS} text-gray-900 lg:whitespace-nowrap`}>
            AI가 분석해주는 나의 음악은?
          </h2>
          <p className="text-base sm:text-lg md:text-xl leading-relaxed text-gray-600 break-keep text-balance">
            <span className="block sm:inline">단 10초 업로드로 음악의 분위기, 감정, DNA 유형까지 한 번에 파악하세요.</span>
            <span className="block sm:inline">분석 결과를 통해 나만의 음악 스타일과 성장 포인트를 확인할 수 있습니다.</span>
          </p>
          <div className="pt-2">
            <Link
              href="/music-analysis/analyze"
              className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-[#4338ca] via-[#7c3aed] to-[#a855f7] px-6 py-3 text-base md:text-base font-semibold text-white shadow-lg shadow-[#7c3aed]/40 transition-transform hover:scale-[1.02]"
            >
              AI 분석 시작하기
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

interface SectionWrapperProps {
  children: React.ReactNode;
  gradientFrom: string;
  gradientTo: string;
  paddingY?: string;
}

function SectionWrapper({
  children,
  gradientFrom,
  gradientTo,
  paddingY = "py-16 sm:py-20 md:py-24 lg:py-32",
}: SectionWrapperProps) {
  // 중간 색상 계산 (더 부드러운 전환을 위해)
  const midColor = blendColors(gradientFrom, gradientTo, 0.5);
  
  return (
    <section
      className={paddingY}
      style={{
        background: `linear-gradient(180deg, ${gradientFrom} 0%, ${midColor} 50%, ${gradientTo} 100%)`,
      }}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6">{children}</div>
    </section>
  );
}

// 두 색상을 혼합하는 유틸리티 함수
function blendColors(color1: string, color2: string, ratio: number): string {
  const hex1 = color1.replace('#', '');
  const hex2 = color2.replace('#', '');
  
  const r1 = parseInt(hex1.substr(0, 2), 16);
  const g1 = parseInt(hex1.substr(2, 2), 16);
  const b1 = parseInt(hex1.substr(4, 2), 16);
  
  const r2 = parseInt(hex2.substr(0, 2), 16);
  const g2 = parseInt(hex2.substr(2, 2), 16);
  const b2 = parseInt(hex2.substr(4, 2), 16);
  
  const r = Math.round(r1 * (1 - ratio) + r2 * ratio);
  const g = Math.round(g1 * (1 - ratio) + g2 * ratio);
  const b = Math.round(b1 * (1 - ratio) + b2 * ratio);
  
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

interface InfoSectionProps {
  kicker: string;
  title: React.ReactNode;
  description: React.ReactNode;
  ctaText: string;
  note: string;
  children: React.ReactNode;
  reverse?: boolean;
}

function InfoSection({
  kicker,
  title,
  description,
  ctaText,
  note,
  children,
  reverse = false,
}: InfoSectionProps) {
  const sectionId =
    kicker?.toLowerCase().includes("ai")
      ? "analyze"
      : kicker?.toLowerCase().includes("pro")
        ? "pro"
        : "community";

  const textContent = (
    <motion.div
      id={sectionId}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3, margin: "-100px" }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className="space-y-6"
    >
      <Kicker>{kicker}</Kicker>
      <h2
        className={`text-3xl sm:text-4xl md:text-5xl font-semibold leading-tight tracking-tight text-white`}
      >
        {title}
      </h2>
      <p
        className="text-white/90 text-base sm:text-lg md:text-xl leading-relaxed max-w-2xl break-keep"
        style={{ wordBreak: "keep-all", overflowWrap: "break-word" }}
      >
        {description}
      </p>
      <div className="flex items-center gap-4 pt-4">
        <Link
          href={
            sectionId === "analyze"
              ? "/music-analysis/analyze"
              : sectionId === "pro"
                ? "/expert-feedback"
                : "/lessons"
          }
        >
          {sectionId === "analyze" ? (
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: "0 0 40px rgba(167,139,250,0.5)" }}
              whileTap={{ scale: 0.98 }}
              className="relative px-8 py-4 rounded-2xl font-semibold text-white backdrop-blur transition-all duration-300"
              style={{
                background: "linear-gradient(135deg, #A78BFA 0%, #FBCFE8 100%)",
                boxShadow: "0 8px 32px rgba(167,139,250,0.4), 0 0 0 1px rgba(251,207,232,0.3)",
              }}
            >
              <span
                className="absolute inset-0 rounded-2xl opacity-50 blur-xl"
                style={{
                  background: "linear-gradient(135deg, #A78BFA 0%, #FBCFE8 100%)",
                }}
              />
              <span className="relative flex items-center gap-2">
                {ctaText}
                <ArrowRight className="w-5 h-5" />
              </span>
            </motion.button>
          ) : sectionId === "community" ? (
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: "0 0 40px rgba(183,156,255,0.5)" }}
              whileTap={{ scale: 0.98 }}
              className="relative px-8 py-4 rounded-2xl font-semibold text-white backdrop-blur transition-all duration-300"
              style={{
                background: "linear-gradient(135deg, #A78BFA 0%, #b79cff 100%)",
                boxShadow: "0 8px 32px rgba(183,156,255,0.4), 0 0 0 1px rgba(183,156,255,0.3)",
              }}
            >
              <span
                className="absolute inset-0 rounded-2xl opacity-50 blur-xl"
                style={{
                  background: "linear-gradient(135deg, #A78BFA 0%, #b79cff 100%)",
                }}
              />
              <span className="relative font-bold text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]">
                {ctaText}
              </span>
            </motion.button>
          ) : (
            <motion.button
              whileHover={{
                scale: 1.05,
                boxShadow: "0 0 40px rgba(124,58,237,0.35)",
              }}
              whileTap={{ scale: 0.98 }}
              className="relative px-8 py-4 rounded-2xl font-semibold text-white shadow-lg transition-all duration-300"
              style={{
                background: "linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)",
              }}
            >
              <span className="relative flex items-center gap-2">
                {ctaText}
                <ArrowRight className="w-5 h-5" />
              </span>
            </motion.button>
          )}
        </Link>
        {note && (
          <span
            className="text-white/60 text-base"
          >
            {note}
          </span>
        )}
      </div>
    </motion.div>
  );

  const cardContent = (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3, margin: "-100px" }}
      transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );

  return (
    <div className="grid md:grid-cols-2 gap-8 sm:gap-12 md:gap-16 items-stretch">
      {reverse ? (
        <>
          {cardContent}
          {textContent}
        </>
      ) : (
        <>
          {textContent}
          {cardContent}
        </>
      )}
    </div>
  );
}

function Kicker({ children }: { children: React.ReactNode }) {
  const isCommunity = typeof children === "string" && children.toLowerCase().includes("community");
  const isPro = typeof children === "string" && children.toLowerCase().includes("pro");
  const isAi = typeof children === "string" && children.toLowerCase().includes("ai");
  const baseColor = isCommunity
    ? "text-[#b79cff]"
    : isPro
      ? "text-[#7C3AED]"
      : isAi
        ? "text-[#7C3AED]"
        : "text-white/70";
  const dotColor = isCommunity ? "#b79cff" : isPro ? "#7C3AED" : isAi ? "#7C3AED" : "#A78BFA";
  return (
    <div className={`inline-flex items-center gap-2 text-base tracking-widest uppercase ${baseColor}`}>
      <span 
        className="h-1.5 w-1.5 rounded-full shadow-[0_0_12px_2px_rgba(183,156,255,0.6)]"
        style={{
          backgroundColor: dotColor,
        }}
      />
      {children}
    </div>
  );
}

function GlowButton({ children }: { children: React.ReactNode }) {
        return (
    <button
      className="relative px-6 py-4 md:px-8 md:py-4 rounded-2xl font-semibold text-base md:text-lg border-2 border-[#FBCFE8]/60 bg-gradient-to-r from-[#A78BFA]/20 via-[#FBCFE8]/20 to-[#A78BFA]/20 text-white backdrop-blur-md transition-all hover:translate-y-[-2px] hover:shadow-[0_8px_40px_rgba(167,139,250,0.5)] hover:border-[#FBCFE8]/80 active:translate-y-0"
      style={{
        boxShadow:
          "0 0 0 1px rgba(251,207,232,0.3), 0 8px 32px rgba(167,139,250,0.4), inset 0 1px 0 rgba(255,255,255,0.1)",
        textShadow: "0 2px 4px rgba(0,0,0,0.2)",
      }}
    >
      <span
        className="absolute inset-0 rounded-2xl opacity-40 blur-xl"
        style={{
          background:
            "linear-gradient(90deg, rgba(167,139,250,0.5), rgba(251,207,232,0.5), rgba(167,139,250,0.5))",
        }}
      />
      <span className="relative font-bold">{children}</span>
    </button>
  );
}

/* --- Cards --- */

function ProducerVideoCard() {
  return (
        <motion.div
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.3 }}
      className="relative w-full h-full min-h-[360px] sm:min-h-[420px] rounded-3xl overflow-hidden shadow-2xl border border-white/10 bg-gradient-to-br from-[#1E1B4B] to-[#0f0e22]"
    >
      <div
        className="absolute inset-0 bg-gradient-to-br from-[#A78BFA]/10 to-[#FBCFE8]/10 opacity-50 blur-2xl pointer-events-none"
      />
      <video
        className="w-full h-full object-cover relative z-10"
        src="/videos/pro_feec.mp4"
        autoPlay
        muted
        loop
        playsInline
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none z-20" />
    </motion.div>
  );
}

function EmotionAnalyzerCard() {
  const [fileName, setFileName] = useState("");
  const [showResult, setShowResult] = useState(false);

  return (
    <div className="relative rounded-3xl p-6 md:p-8 bg-gradient-to-br from-[#1E1B4B] to-[#0f0e22] border border-white/10 shadow-2xl overflow-hidden">
      <div
        className="absolute -bottom-10 -left-10 h-64 w-64 rounded-full blur-2xl opacity-20"
        style={{
          background:
            "radial-gradient(60% 60% at 50% 50%, #FBCFE8 0%, rgba(251,207,232,0.0) 70%)",
        }}
      />

      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-base text-white/70">파일 업로드</p>
          <p className="text-lg font-medium">Emotion Analysis</p>
        </div>
      </div>

      <div className="mt-4 grid gap-3">
        <label className="cursor-pointer inline-flex items-center gap-3 px-4 py-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition">
          <input
            type="file"
            accept="audio/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) {
                setFileName(f.name);
                setShowResult(true);
              }
            }}
          />
          <span className="text-base">오디오 선택하기</span>
          <span className="text-base text-white/50">
            {fileName || "mp3, wav 지원"}
          </span>
        </label>
        <div className="text-base text-white/50">
          * 데모 모드: 실제 업로드 없이 UI만 체험
        </div>
      </div>

        <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: showResult ? 1 : 0, y: showResult ? 0 : 12 }}
        transition={{ duration: 0.6 }}
        className="mt-6"
      >
        {showResult && (
          <>
            <EmotionSpectrum />
            <MoodChips chips={["Warm", "Bittersweet", "Hopeful", "Nocturne"]} />
          </>
        )}
        </motion.div>
      </div>
  );
}

function ProducerFeedbackCard() {
  const [tab, setTab] = useState("overview");
  const [pathData, setPathData] = useState<string>("");
  const [mounted, setMounted] = useState(false);

  const feedbackContent = {
    overview: {
      text: "감정 곡선은 1:20 지점에서 고조됩니다. 코러스 전 4마디를 축약하면 집중도가 상승할 것 같아요.",
      highlight: "1:20",
      highlightPosition: 0.5, // 1:20 / 2:40 = 0.5
    },
    tone: {
      text: "보컬 톤은 따뜻하지만 약간의 공기감이 부족합니다. 8kHz 대역을 살짝 들어 올리고, 리버브 테일을 0.2s 줄여보세요.",
      highlight: "8kHz",
      highlightPosition: 0.3,
    },
    mix: {
      text: "킥과 베이스의 충돌을 사이드체인으로 완화하고, 스테레오 이미지가 과하면 중저역을 모노에 가깝게 정리하세요.",
      highlight: "사이드체인",
      highlightPosition: 0.4,
    },
  };

  const currentContent = feedbackContent[tab as keyof typeof feedbackContent];

  // 클라이언트 사이드에서만 path 생성 (SSR hydration 불일치 방지)
  useEffect(() => {
    setMounted(true);
    const generateCurvePath = () => {
      const width = 260;
      const height = 90;
      const centerY = height / 2;
      const amplitude = 30;
      const segments = 100;
      
      let path = `M 0 ${centerY}`;
      
      for (let i = 0; i <= segments; i++) {
        const x = (i / segments) * width;
        const normalizedX = (i / segments) * Math.PI * 3;
        // 부드러운 sine wave + 변형
        const y = centerY - Math.sin(normalizedX) * amplitude + Math.sin(normalizedX * 2) * 8;
        path += ` L ${x} ${y}`;
      }
      
      return path;
    };

    setPathData(generateCurvePath());
  }, []);

  const highlightX = currentContent.highlightPosition * 260;
  const highlightY = mounted ? 45 - Math.sin(currentContent.highlightPosition * Math.PI * 3) * 30 + Math.sin(currentContent.highlightPosition * Math.PI * 6) * 8 : 45;

  return (
    <motion.div
      whileHover={{ scale: 1.03 }}
      transition={{ duration: 0.5, ease: "easeInOut" }}
      className="relative w-full max-w-[640px] rounded-2xl bg-gradient-to-br from-[#0F0E17] to-[#1B1730] shadow-[0_0_25px_rgba(167,139,250,0.15)] p-4 sm:p-6 pb-8 sm:pb-6 flex flex-col sm:flex-row justify-between items-start gap-4 sm:gap-6 overflow-visible group"
    >
      {/* 배경 글로우 효과 (hover 시) */}
      <div className="absolute inset-0 bg-[#A78BFA]/0 group-hover:bg-[#A78BFA]/10 transition-all duration-500 rounded-2xl pointer-events-none" />

      {/* 왼쪽 섹션 - 텍스트 영역 */}
      <div className="flex-1 flex flex-col h-full">
        <div>
          <p className="text-base text-[#C4B5FD] font-medium">프로듀서 매칭</p>
          <p className="text-xl font-bold text-white mt-1">Emotion-led Review</p>
                </div>
                
        {/* 태그 버튼 */}
        <div className="inline-flex gap-2 mt-3 flex-wrap">
          {[
            { id: "overview", label: "요약" },
            { id: "tone", label: "톤 & 무드" },
            { id: "mix", label: "믹스" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`rounded-full px-3 py-1 text-base border transition-all ${
                tab === t.id
                  ? "bg-[#A78BFA]/10 text-[#A78BFA] border-[#A78BFA]/40"
                  : "bg-white/5 text-white/70 hover:bg-[#A78BFA]/10 hover:text-[#A78BFA] border-transparent hover:border-[#A78BFA]/40"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* 피드백 텍스트 */}
          <motion.div
          key={tab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mt-4 text-base text-[#E9E9F5]/80 leading-relaxed flex-1 break-keep"
          style={{ wordBreak: "keep-all", overflowWrap: "break-word" }}
        >
          <p>
            {currentContent.text.split(currentContent.highlight).map((part, i, arr) => (
              <span key={i}>
                {part}
                {i < arr.length - 1 && (
                  <span className="text-[#A78BFA] font-medium">
                    {currentContent.highlight}
                  </span>
                )}
              </span>
            ))}
            </p>
          </motion.div>
      </div>

      {/* 오른쪽 섹션 - 감정 곡선 그래프 */}
      <div className="flex-shrink-0 w-full sm:w-[260px] h-[90px] relative mx-auto sm:mx-0 mb-6 sm:mb-0">
        {/* SVG 곡선 */}
        {mounted && pathData ? (
          <svg
            width="260"
            height="90"
            viewBox="0 0 260 90"
            className="w-full h-full"
            style={{ filter: "drop-shadow(0 0 8px rgba(167,139,250,0.3))" }}
            preserveAspectRatio="xMidYMid meet"
          >
            <defs>
              <linearGradient id="emotionGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#A78BFA" />
                <stop offset="100%" stopColor="#FBCFE8" />
              </linearGradient>
            </defs>
            
            {/* 베이스 라인 */}
            <line
              x1="0"
              y1="45"
              x2="260"
              y2="45"
              stroke="rgba(255,255,255,0.1)"
              strokeWidth="1"
            />

            {/* 감정 곡선 */}
            <motion.path
              d={pathData}
              fill="none"
              stroke="url(#emotionGradient)"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity="0.8"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 2, ease: "easeOut" }}
              style={{
                strokeDasharray: 1000,
                strokeDashoffset: 0,
              }}
            />

            {/* 하이라이트 점 */}
            {tab === "overview" && (
              <g>
                <motion.circle
                  cx={highlightX}
                  cy={highlightY}
                  r="4"
                  fill="#C084FC"
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: [0.5, 1, 0.5], scale: [1, 1.3, 1] }}
                  transition={{
                    duration: 1.8,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
                {/* 하이라이트 점 주변 글로우 */}
                <circle
                  cx={highlightX}
                  cy={highlightY}
                  r="8"
                  fill="#C084FC"
                  opacity="0.2"
                />
              </g>
            )}
          </svg>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="w-full h-1 bg-white/10 rounded-full" />
          </div>
        )}

        {/* 타임라인 텍스트 */}
        <div className="absolute bottom-0 left-0 right-0 flex justify-between text-base font-mono text-gray-400/70">
          <span>0:00</span>
          <span>2:40</span>
        </div>

        {/* 하단 라벨 */}
        <div className="absolute -bottom-5 right-0 text-base italic text-gray-500/60">
          AI Emotion Curve
        </div>
      </div>
    </motion.div>
  );
}

function CommunityIconCard() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
      className="relative flex items-center justify-center w-full"
    >
      <motion.div
        whileHover={{ scale: 1.05 }}
        transition={{ duration: 0.3 }}
        className="relative w-full max-w-md mx-auto"
      >
        <Image
          src="/images/icon.png"
          alt="ConnectTone Icon"
          width={400}
          height={400}
          className="object-contain w-full h-auto"
          style={{
            filter: "drop-shadow(0 0 20px rgba(183, 156, 255, 0.3))",
          }}
          unoptimized
        />
      </motion.div>
    </motion.div>
  );
}

/* --- Visual helpers --- */

function WaveformDemo() {
  const bars = Array.from({ length: 60 }, (_, i) => 8 + Math.abs(Math.sin(i * 0.3)) * 40);

  return (
    <div className="mt-6">
      <div className="h-24 w-full rounded-xl bg-white/5 border border-white/10 overflow-hidden flex items-end gap-1 p-2">
        {bars.map((height, i) => (
          <span
            key={i}
            style={{ height: `${height}px`, opacity: 0.9 }}
            className="flex-1 rounded-full bg-gradient-to-t from-[#A78BFA] to-[#FBCFE8]"
          />
        ))}
      </div>
    </div>
  );
}

function EmotionSpectrum() {
  const bars = [20, 45, 70, 50, 30, 55, 80];
  const labels = ["Calm", "Warm", "Bittersweet", "Hope", "Release", "Glow", "Elation"];

  return (
    <div>
      <div className="grid grid-cols-7 gap-2 items-end h-32">
        {bars.map((h, i) => (
          <motion.div
            key={i}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: `${h}%`, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.05 * i }}
            className="rounded-xl bg-gradient-to-t from-[#A78BFA] to-[#FBCFE8] border border-white/10"
            title={`${labels[i]} ${h}%`}
          />
        ))}
      </div>
      <div className="mt-2 grid grid-cols-7 text-[11px] text-center text-white/70">
        {labels.map((l) => (
          <span key={l}>{l}</span>
        ))}
      </div>
    </div>
  );
}

function MoodChips({ chips }: { chips: string[] }) {
  return (
    <div className="mt-4 flex flex-wrap gap-2">
      {chips.map((c) => (
        <span
          key={c}
          className="px-3 py-1.5 rounded-xl text-base border border-white/10 bg-white/5 hover:bg-white/10 transition"
        >
          {c}
              </span>
      ))}
      </div>
  );
}

function BrandHeader() {
  return (
    <section className="relative py-24 sm:py-32 md:py-40 lg:py-48 overflow-hidden">
      {/* Background Video */}
      <div className="absolute inset-0 w-full h-full overflow-hidden">
        <div className="relative w-full h-full">
          <video
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full object-cover"
            src="/videos/main2.mp4"
            autoPlay
            muted
            loop
            playsInline
            style={{ 
              pointerEvents: 'none',
              minWidth: '100%',
              minHeight: '100%',
              width: 'auto',
              height: 'auto'
            }}
          />
          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-secondary/60 via-secondary/50 to-secondary/60" />
        </div>
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6">
        <div className="flex justify-center items-center">
          {/* Brand Tagline and Name with staggered animations */}
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-white leading-tight mb-4">
              {/* 모든 음악이 모여, */}
              <motion.div
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ 
                  duration: 1.0,
                  delay: 0.2,
                  ease: [0.25, 0.46, 0.45, 0.94]
                }}
                className="inline-block"
              >
                모든 음악이 모여,
              </motion.div>
              <br />
              {/* 하나로 */}
              <motion.div
                className="bg-gradient-to-r from-point via-point2 to-point3 bg-clip-text text-transparent inline-block"
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ 
                  duration: 1.0,
                  delay: 0.6,
                  ease: [0.25, 0.46, 0.45, 0.94]
                }}
              >
                하나로
              </motion.div>{" "}
              {/* 연결되다. */}
              <motion.div
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ 
                  duration: 1.0,
                  delay: 1.0,
                  ease: [0.25, 0.46, 0.45, 0.94]
                }}
                className="inline-block"
              >
                연결되다.
              </motion.div>
            </h1>
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ 
                duration: 0.8,
                delay: 1.1,
                ease: [0.25, 0.46, 0.45, 0.94]
              }}
            >
              <p className="text-lg sm:text-xl md:text-2xl text-white/90 font-medium">
                Connect on, Connect one,
                <br />
                ConnecTone.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}



